/**
 * Player Intelligence Achievement framework (Phase 2.2 polish).
 * Projection-only — no Firestore achievements collection.
 */

import { computeActiveDayStreak, formatMatchWhenShort } from "../profile/profileDashboardStats";
import { normalizeSportId, DEFAULT_SPORT } from "../sport/sportTypes";

/** @typedef {'career' | 'winning' | 'activity' | 'sport'} AchievementGroup */

/**
 * @typedef {{
 *   id: string,
 *   group: AchievementGroup,
 *   title: string,
 *   detail: string,
 *   icon: string,
 *   target?: number,
 *   progress?: number,
 *   unlocked: boolean,
 *   unlockedAt?: number | null,
 *   whenLabel?: string,
 * }} AchievementView
 */

export const ACHIEVEMENT_GROUPS = Object.freeze({
  career: { id: "career", label: "Career" },
  winning: { id: "winning", label: "Winning" },
  activity: { id: "activity", label: "Activity" },
  sport: { id: "sport", label: "Sport" },
});

/**
 * @param {Array<object>} cards newest-first
 */
function chronological(cards) {
  return [...(cards || [])].sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
}

/**
 * @param {Array<object>} cards
 * @param {number} n 1-based
 */
function nthMatchAt(cards, n) {
  const chrono = chronological(cards);
  return chrono[n - 1]?.completedAt || null;
}

/**
 * @param {Array<object>} cards
 * @param {number} n 1-based win count
 */
function nthWinAt(cards, n) {
  const wins = chronological(cards).filter((c) => c.isWin);
  return wins[n - 1]?.completedAt || null;
}

/**
 * Days of week / hour heuristics for activity achievements.
 * @param {Array<object>} cards
 */
function activitySignals(cards) {
  const dayKeys = new Set();
  let weekendMatches = 0;
  let morningMatches = 0;
  let nightMatches = 0;
  /** @type {Map<string, Set<string>>} year-month → day keys */
  const monthDays = new Map();

  (cards || []).forEach((c) => {
    if (!c.completedAt) return;
    const d = new Date(c.completedAt);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    dayKeys.add(dayKey);
    const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
    if (!monthDays.has(monthKey)) monthDays.set(monthKey, new Set());
    monthDays.get(monthKey).add(dayKey);

    const dow = d.getDay();
    if (dow === 0 || dow === 6) weekendMatches += 1;
    const hour = d.getHours();
    if (hour >= 5 && hour < 11) morningMatches += 1;
    if (hour >= 20 || hour < 5) nightMatches += 1;
  });

  let bestMonthDays = 0;
  monthDays.forEach((set) => {
    if (set.size > bestMonthDays) bestMonthDays = set.size;
  });

  return {
    activityDays: dayKeys.size,
    bestMonthDays,
    weekendMatches,
    morningMatches,
    nightMatches,
    activeDayStreak: computeActiveDayStreak(cards),
  };
}

/**
 * Longest consecutive winning streak end timestamp (approx = last win of best streak).
 * @param {Array<object>} cards newest-first
 */
function longestStreakUnlockedAt(cards) {
  const chrono = chronological(cards);
  let best = 0;
  let current = 0;
  let bestEnd = null;
  chrono.forEach((c) => {
    if (c.isWin) {
      current += 1;
      if (current > best) {
        best = current;
        bestEnd = c.completedAt || null;
      }
    } else {
      current = 0;
    }
  });
  return { length: best, at: bestEnd };
}

/**
 * Core catalog achievements (sport-agnostic).
 * @param {object} stats enriched career stats
 * @param {Array<object>} cards
 * @returns {AchievementView[]}
 */
export function evaluateCoreAchievements(stats, cards) {
  const list = cards || [];
  const matches = stats.matchesPlayed ?? list.length;
  const wins = stats.wins ?? list.filter((c) => c.isWin).length;
  const streakInfo = longestStreakUnlockedAt(list);
  const longestStreak = stats.longestWinningStreak ?? streakInfo.length;
  const activity = activitySignals(list);

  /** @type {AchievementView[]} */
  const out = [];

  const push = (def) => {
    const unlocked = Boolean(def.unlocked);
    out.push({
      id: def.id,
      group: def.group,
      title: def.title,
      detail: unlocked ? def.detailUnlocked : def.detailLocked,
      icon: def.icon,
      target: def.target,
      progress: def.progress,
      unlocked,
      unlockedAt: unlocked ? def.unlockedAt || null : null,
      whenLabel: unlocked && def.unlockedAt ? formatMatchWhenShort(def.unlockedAt) : undefined,
    });
  };

  // —— Career ——
  const careerThresholds = [
    { id: "first_match", title: "First Match", n: 1, icon: "flag", detail: "Career match recorded" },
    { id: "matches_10", title: "10 Matches", n: 10, icon: "hash", detail: "Double-digit career matches" },
    { id: "matches_50", title: "50 Matches", n: 50, icon: "layers", detail: "Half-century of matches" },
    { id: "matches_100", title: "100 Matches", n: 100, icon: "award", detail: "Century of matches" },
    { id: "matches_250", title: "250 Matches", n: 250, icon: "trophy", detail: "Quarter-thousand matches" },
    { id: "matches_500", title: "500 Matches", n: 500, icon: "sparkles", detail: "Half-thousand matches" },
  ];
  careerThresholds.forEach((t) => {
    const unlocked = matches >= t.n;
    push({
      id: t.id,
      group: "career",
      title: t.title,
      icon: t.icon,
      target: t.n,
      progress: Math.min(matches, t.n),
      unlocked,
      unlockedAt: unlocked ? nthMatchAt(list, t.n) : null,
      detailUnlocked: t.detail,
      detailLocked: `Complete ${t.n} match${t.n === 1 ? "" : "es"}`,
    });
  });

  // —— Winning ——
  push({
    id: "first_win",
    group: "winning",
    title: "First Win",
    icon: "check",
    target: 1,
    progress: Math.min(wins, 1),
    unlocked: wins >= 1,
    unlockedAt: nthWinAt(list, 1),
    detailUnlocked: "Opened the win column",
    detailLocked: "Win your first match",
  });

  [
    { id: "streak_3", title: "3 Match Streak", n: 3, icon: "zap" },
    { id: "streak_5", title: "5 Match Streak", n: 5, icon: "flame" },
    { id: "streak_10", title: "10 Match Streak", n: 10, icon: "trendingUp" },
  ].forEach((t) => {
    const unlocked = longestStreak >= t.n;
    push({
      id: t.id,
      group: "winning",
      title: t.title,
      icon: t.icon,
      target: t.n,
      progress: Math.min(longestStreak, t.n),
      unlocked,
      unlockedAt: unlocked ? streakInfo.at : null,
      detailUnlocked: `${longestStreak}-match winning streak`,
      detailLocked: `Win ${t.n} matches in a row`,
    });
  });

  [
    { id: "wins_25", title: "25 Wins", n: 25, icon: "medal" },
    { id: "wins_50", title: "50 Wins", n: 50, icon: "star" },
    { id: "wins_100", title: "100 Wins", n: 100, icon: "crown" },
  ].forEach((t) => {
    const unlocked = wins >= t.n;
    push({
      id: t.id,
      group: "winning",
      title: t.title,
      icon: t.icon,
      target: t.n,
      progress: Math.min(wins, t.n),
      unlocked,
      unlockedAt: unlocked ? nthWinAt(list, t.n) : null,
      detailUnlocked: `${t.n} career wins`,
      detailLocked: `Reach ${t.n} wins`,
    });
  });

  // —— Activity ——
  push({
    id: "month_5_days",
    group: "activity",
    title: "Played 5 days in a month",
    icon: "calendar",
    target: 5,
    progress: Math.min(activity.bestMonthDays, 5),
    unlocked: activity.bestMonthDays >= 5,
    unlockedAt: list[0]?.completedAt || null,
    detailUnlocked: `${activity.bestMonthDays} active days in a month`,
    detailLocked: "Play on 5 different days in one month",
  });
  push({
    id: "activity_10_days",
    group: "activity",
    title: "Played 10 days",
    icon: "calendar",
    target: 10,
    progress: Math.min(activity.activityDays, 10),
    unlocked: activity.activityDays >= 10,
    unlockedAt: list[0]?.completedAt || null,
    detailUnlocked: `${activity.activityDays} career activity days`,
    detailLocked: "Play on 10 different days",
  });
  push({
    id: "weekend_warrior",
    group: "activity",
    title: "Weekend Warrior",
    icon: "sun",
    target: 3,
    progress: Math.min(activity.weekendMatches, 3),
    unlocked: activity.weekendMatches >= 3,
    unlockedAt: list[0]?.completedAt || null,
    detailUnlocked: `${activity.weekendMatches} weekend matches`,
    detailLocked: "Complete 3 weekend matches",
  });
  push({
    id: "morning_player",
    group: "activity",
    title: "Morning Player",
    icon: "sunrise",
    target: 3,
    progress: Math.min(activity.morningMatches, 3),
    unlocked: activity.morningMatches >= 3,
    unlockedAt: list[0]?.completedAt || null,
    detailUnlocked: `${activity.morningMatches} morning sessions`,
    detailLocked: "Play 3 matches before 11 AM",
  });
  push({
    id: "night_owl",
    group: "activity",
    title: "Night Owl",
    icon: "moon",
    target: 3,
    progress: Math.min(activity.nightMatches, 3),
    unlocked: activity.nightMatches >= 3,
    unlockedAt: list[0]?.completedAt || null,
    detailUnlocked: `${activity.nightMatches} late sessions`,
    detailLocked: "Play 3 matches after 8 PM",
  });

  return out;
}

/**
 * Parse set columns from a card for sport heuristics.
 * @param {object} card
 * @returns {Array<{ a: number, b: number }>}
 */
export function parseCardSetScores(card) {
  if (card?.setColumns?.length) {
    return card.setColumns.map((col) => {
      const myTeam = card.myTeam === "B" ? "B" : "A";
      const my = Number(col.my) || 0;
      const opp = Number(col.opp) || 0;
      return myTeam === "B" ? { a: opp, b: my } : { a: my, b: opp };
    });
  }
  const sets = [];
  if (card?.scoreSummary) {
    String(card.scoreSummary)
      .split(",")
      .forEach((part) => {
        const m = part.trim().match(/^(\d+)\s*[-–]\s*(\d+)$/);
        if (!m) return;
        sets.push({ a: Number(m[1]), b: Number(m[2]) });
      });
  }
  return sets;
}

/** Badminton — straight-games win (smash-style decisive win when computable). */
export function projectBadmintonSportAchievements(cards) {
  /** @type {AchievementView[]} */
  const list = [];
  const chrono = chronological(cards);
  let unlockedAt = null;
  chrono.forEach((c) => {
    if (!c.isWin || unlockedAt) return;
    const sets = parseCardSetScores(c);
    if (sets.length >= 2 && sets.every((s) => s.a > s.b || s.b > s.a)) {
      const myTeam = c.myTeam === "B" ? "B" : "A";
      const allMine = sets.every((s) => (myTeam === "A" ? s.a > s.b : s.b > s.a));
      if (allMine) unlockedAt = c.completedAt || null;
    }
  });
  list.push({
    id: "badminton_first_smash_victory",
    group: "sport",
    title: "First Smash Victory",
    detail: unlockedAt ? "Straight-games win recorded" : "Win a match in straight games",
    icon: "zap",
    target: 1,
    progress: unlockedAt ? 1 : 0,
    unlocked: Boolean(unlockedAt),
    unlockedAt,
    whenLabel: unlockedAt ? formatMatchWhenShort(unlockedAt) : undefined,
  });
  return list;
}

/** Tennis — first tiebreak win (7–6 set). */
export function projectTennisSportAchievements(cards) {
  let unlockedAt = null;
  chronological(cards).forEach((c) => {
    if (!c.isWin || unlockedAt) return;
    const sets = parseCardSetScores(c);
    const myTeam = c.myTeam === "B" ? "B" : "A";
    const hit = sets.some((s) => {
      const my = myTeam === "A" ? s.a : s.b;
      const opp = myTeam === "A" ? s.b : s.a;
      return my === 7 && opp === 6;
    });
    if (hit) unlockedAt = c.completedAt || null;
  });
  return [
    {
      id: "tennis_first_tiebreak_win",
      group: "sport",
      title: "First Tiebreak Win",
      detail: unlockedAt ? "Won a 7–6 set" : "Win a set 7–6",
      icon: "target",
      target: 1,
      progress: unlockedAt ? 1 : 0,
      unlocked: Boolean(unlockedAt),
      unlockedAt,
      whenLabel: unlockedAt ? formatMatchWhenShort(unlockedAt) : undefined,
    },
  ];
}

/** Pickleball — first comeback after losing game 1. */
export function projectPickleballSportAchievements(cards) {
  let unlockedAt = null;
  chronological(cards).forEach((c) => {
    if (!c.isWin || unlockedAt) return;
    const sets = parseCardSetScores(c);
    if (sets.length < 2) return;
    const myTeam = c.myTeam === "B" ? "B" : "A";
    const g1 = sets[0];
    const lostFirst = myTeam === "A" ? g1.a < g1.b : g1.b < g1.a;
    if (lostFirst) unlockedAt = c.completedAt || null;
  });
  return [
    {
      id: "pickleball_first_sideout_comeback",
      group: "sport",
      title: "First Side-Out Comeback",
      detail: unlockedAt ? "Won after dropping game one" : "Win a match after losing game 1",
      icon: "refresh",
      target: 1,
      progress: unlockedAt ? 1 : 0,
      unlocked: Boolean(unlockedAt),
      unlockedAt,
      whenLabel: unlockedAt ? formatMatchWhenShort(unlockedAt) : undefined,
    },
  ];
}

/** Table tennis — first deuce victory (both ≥ 10, win by 2). */
export function projectTableTennisSportAchievements(cards) {
  let unlockedAt = null;
  chronological(cards).forEach((c) => {
    if (!c.isWin || unlockedAt) return;
    const sets = parseCardSetScores(c);
    const myTeam = c.myTeam === "B" ? "B" : "A";
    const hit = sets.some((s) => {
      const my = myTeam === "A" ? s.a : s.b;
      const opp = myTeam === "A" ? s.b : s.a;
      return my >= 10 && opp >= 10 && my - opp >= 2;
    });
    if (hit) unlockedAt = c.completedAt || null;
  });
  return [
    {
      id: "tt_first_deuce_victory",
      group: "sport",
      title: "First Deuce Victory",
      detail: unlockedAt ? "Won a deuce game" : "Win a game after both reach 10",
      icon: "activity",
      target: 1,
      progress: unlockedAt ? 1 : 0,
      unlocked: Boolean(unlockedAt),
      unlockedAt,
      whenLabel: unlockedAt ? formatMatchWhenShort(unlockedAt) : undefined,
    },
  ];
}

/**
 * Full achievement projection for a sport filter.
 * @param {object} stats
 * @param {Array<object>} cards
 * @param {Array<AchievementView>} [sportAchievements]
 */
export function projectAchievementFramework(stats, cards, sportAchievements = []) {
  const core = evaluateCoreAchievements(stats, cards);
  const sport = (sportAchievements || []).map((a) => ({
    ...a,
    group: "sport",
  }));
  const all = [...core, ...sport];
  const unlocked = all.filter((a) => a.unlocked);
  const locked = all.filter((a) => !a.unlocked);
  const byGroup = {
    career: all.filter((a) => a.group === "career"),
    winning: all.filter((a) => a.group === "winning"),
    activity: all.filter((a) => a.group === "activity"),
    sport: all.filter((a) => a.group === "sport"),
  };
  return {
    all,
    unlocked: unlocked.sort((a, b) => (b.unlockedAt || 0) - (a.unlockedAt || 0)),
    locked,
    byGroup,
    unlockedCount: unlocked.length,
    totalCount: all.length,
  };
}

/**
 * Major career milestones in timeline order (oldest → newest for story; UI may reverse).
 * @param {object} stats
 * @param {Array<object>} cards
 */
export function projectCareerMilestones(stats, cards) {
  const list = cards || [];
  const chrono = chronological(list);
  /** @type {Array<{ id: string, title: string, detail: string, at: number, whenLabel: string, tone: string }>} */
  const milestones = [];

  const pushAt = (id, title, detail, at, tone = "amber") => {
    if (!at) return;
    milestones.push({
      id,
      title,
      detail,
      at,
      whenLabel: formatMatchWhenShort(at),
      tone,
    });
  };

  if (chrono[0]) {
    pushAt("ms_first_match", "First match", "Journey begins", chrono[0].completedAt, "amber");
  }
  const firstWin = chrono.find((c) => c.isWin);
  if (firstWin) {
    pushAt("ms_first_win", "First win", "Opened the win column", firstWin.completedAt, "win");
  }

  [10, 50, 100, 250, 500].forEach((n) => {
    const hit = chrono[n - 1];
    if (hit) {
      pushAt(`ms_match_${n}`, `${n}th match`, "Career volume milestone", hit.completedAt, "amber");
    }
  });

  const wins = chrono.filter((c) => c.isWin);
  [25, 50, 100].forEach((n) => {
    const hit = wins[n - 1];
    if (hit) {
      pushAt(`ms_win_${n}`, `${n}th win`, "Winning milestone", hit.completedAt, "win");
    }
  });

  const streak = longestStreakUnlockedAt(list);
  if (streak.length >= 3 && streak.at) {
    pushAt(
      "ms_longest_streak",
      "Longest winning streak",
      `${streak.length}-match streak`,
      streak.at,
      "win"
    );
  }

  if (stats.fastestMatchSec != null) {
    const card = list.find((c) => c.durationSec === stats.fastestMatchSec);
    pushAt(
      "ms_fastest",
      "Fastest match",
      stats.fastestMatchLabel || "Personal best",
      card?.completedAt || list[0]?.completedAt,
      "purple"
    );
  }
  if (stats.longestMatchSec != null && stats.longestMatchSec !== stats.fastestMatchSec) {
    const card = list.find((c) => c.durationSec === stats.longestMatchSec);
    pushAt(
      "ms_longest",
      "Longest match",
      stats.longestMatchLabel || "Personal best",
      card?.completedAt || list[0]?.completedAt,
      "purple"
    );
  }

  const firstTournament = chrono.find((c) => c.competitionId);
  if (firstTournament) {
    pushAt(
      "ms_first_tournament",
      "First tournament match",
      "Competition play",
      firstTournament.completedAt,
      "purple"
    );
  }

  // Newest first for feed-style UI
  return milestones.sort((a, b) => (b.at || 0) - (a.at || 0));
}

/**
 * Activity / contribution calendar for a month.
 * @param {Array<object>} cards
 * @param {{ year?: number, month?: number }} [opts] month 0-11
 */
export function buildActivityCalendar(cards, opts = {}) {
  const now = new Date();
  const year = opts.year ?? now.getFullYear();
  const month = opts.month ?? now.getMonth();
  /** @type {Map<string, number>} */
  const counts = new Map();
  let monthMatches = 0;

  (cards || []).forEach((c) => {
    if (!c.completedAt) return;
    const d = new Date(c.completedAt);
    if (d.getFullYear() !== year || d.getMonth() !== month) return;
    monthMatches += 1;
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay(); // 0 Sun
  /** @type {Array<{ day: number | null, count: number, level: number, key: string | null }>} */
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) {
    cells.push({ day: null, count: 0, level: 0, key: null });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const key = `${year}-${month}-${day}`;
    const count = counts.get(key) || 0;
    const level = count === 0 ? 0 : count === 1 ? 1 : count <= 3 ? 2 : 3;
    cells.push({ day, count, level, key });
  }

  const allDayKeys = new Set();
  (cards || []).forEach((c) => {
    if (!c.completedAt) return;
    const d = new Date(c.completedAt);
    allDayKeys.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  });

  // Longest activity streak (calendar days with ≥1 match)
  const dayList = [...allDayKeys]
    .map((k) => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m, d).getTime();
    })
    .sort((a, b) => a - b);
  let longestActivityStreak = 0;
  let run = 0;
  let prev = null;
  dayList.forEach((t) => {
    if (prev != null && t - prev === 86400000) run += 1;
    else run = 1;
    if (run > longestActivityStreak) longestActivityStreak = run;
    prev = t;
  });

  const monthLabel = new Date(year, month, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  return {
    year,
    month,
    monthLabel,
    cells,
    activeDaysInMonth: counts.size,
    monthMatches,
    currentStreak: computeActiveDayStreak(cards),
    longestActivityStreak,
    totalActivityDays: allDayKeys.size,
  };
}

/**
 * Personal bests — only metrics computable from cards / sport.
 * @param {object} stats
 * @param {Array<object>} cards
 * @param {string} [sportFilter]
 */
export function projectPersonalBests(stats, cards, sportFilter = "overall") {
  const list = cards || [];
  /** @type {Array<{ id: string, label: string, value: string, detail?: string }>} */
  const bests = [];

  if (stats.longestMatchLabel && stats.longestMatchLabel !== "—") {
    bests.push({
      id: "longest_match",
      label: "Longest match",
      value: stats.longestMatchLabel,
    });
  }
  if (stats.fastestMatchLabel && stats.fastestMatchLabel !== "—") {
    bests.push({
      id: "shortest_match",
      label: "Shortest match",
      value: stats.fastestMatchLabel,
    });
  }
  if ((stats.longestWinningStreak || 0) >= 1) {
    bests.push({
      id: "longest_streak",
      label: "Longest winning streak",
      value: String(stats.longestWinningStreak),
    });
  }

  let bestMargin = null;
  let bestMarginCard = null;
  let mostPoints = null;
  let mostGames = null;
  let mostSets = null;
  let biggestComeback = null;

  list.forEach((c) => {
    const sets = parseCardSetScores(c);
    if (!sets.length) return;
    const myTeam = c.myTeam === "B" ? "B" : "A";
    let pointsMy = 0;
    let pointsOpp = 0;
    let gamesMy = 0;
    let gamesOpp = 0;
    sets.forEach((s) => {
      const my = myTeam === "A" ? s.a : s.b;
      const opp = myTeam === "A" ? s.b : s.a;
      pointsMy += my;
      pointsOpp += opp;
      if (my > opp) gamesMy += 1;
      else if (opp > my) gamesOpp += 1;
    });
    const margin = pointsMy - pointsOpp;
    if (c.isWin && (bestMargin == null || margin > bestMargin)) {
      bestMargin = margin;
      bestMarginCard = c;
    }
    if (mostPoints == null || pointsMy > mostPoints) mostPoints = pointsMy;
    if (mostGames == null || gamesMy > mostGames) mostGames = gamesMy;
    if (mostSets == null || gamesMy > mostSets) mostSets = gamesMy;

    if (c.isWin && sets.length >= 2) {
      const g1 = sets[0];
      const lostFirst = myTeam === "A" ? g1.a < g1.b : g1.b < g1.a;
      if (lostFirst) {
        const deficit = myTeam === "A" ? g1.b - g1.a : g1.a - g1.b;
        if (biggestComeback == null || deficit > biggestComeback) {
          biggestComeback = deficit;
        }
      }
    }
  });

  if (biggestComeback != null && biggestComeback > 0) {
    bests.push({
      id: "biggest_comeback",
      label: "Biggest comeback",
      value: `−${biggestComeback} game 1`,
      detail: "Won after trailing first game",
    });
  }
  if (bestMargin != null && bestMargin > 0 && bestMarginCard) {
    bests.push({
      id: "highest_scoring_edge",
      label: "Highest scoring edge",
      value: `+${bestMargin} pts`,
      detail: bestMarginCard.scoreSummary || undefined,
    });
  }
  if (mostPoints != null) {
    bests.push({ id: "most_points", label: "Most points", value: String(mostPoints) });
  }
  if (mostGames != null && mostGames > 0) {
    const sport = sportFilter && sportFilter !== "overall" ? normalizeSportId(sportFilter) : null;
    const label =
      sport === "tennis" ? "Most sets" : sport === "table_tennis" ? "Most games" : "Most games";
    bests.push({ id: "most_games", label, value: String(mostGames) });
  }
  if ((stats.setsWon || 0) > 0) {
    bests.push({ id: "sets_won", label: "Sets won (career)", value: String(stats.setsWon) });
  }

  return bests.slice(0, 8);
}

/**
 * Story-rich activity timeline (newest first).
 * @param {Array<object>} cards
 * @param {object} stats
 * @param {object} [achievementBundle] from projectAchievementFramework
 * @param {Array<object>} [milestones]
 */
export function projectStoryTimeline(cards, stats, achievementBundle = null, milestones = []) {
  const list = cards || [];
  const chrono = chronological(list);
  /** @type {Array<{ id: string, kind: string, title: string, detail?: string, at: number, whenLabel: string, tone: string }>} */
  const events = [];

  const sportsSeen = new Set();
  chrono.forEach((c, idx) => {
    const sportId = normalizeSportId(c.sportId || DEFAULT_SPORT);
    const opp = c.opponentsLabel || c.opponentsTeamLine || "opponent";
    const typeLabel = c.matchTypeLabel || c.matchType || "Match";

    if (!sportsSeen.has(sportId)) {
      sportsSeen.add(sportId);
      events.push({
        id: `sport_start_${sportId}_${c.matchId}`,
        kind: "sport_started",
        title: `Started ${sportId.replace("_", " ")}`,
        detail: "First match in this sport",
        at: c.completedAt || 0,
        tone: "amber",
      });
    }

    if (idx === 0) {
      events.push({
        id: `career_start_${c.matchId}`,
        kind: "milestone",
        title: "Career begins",
        detail: typeLabel,
        at: c.completedAt || 0,
        tone: "amber",
      });
    }

    if (c.isWin) {
      events.push({
        id: `win_${c.matchId}`,
        kind: "result",
        title: `Won against ${opp}`,
        detail: [c.scoreSummary, typeLabel].filter(Boolean).join(" · "),
        at: c.completedAt || 0,
        tone: "win",
      });
    } else {
      events.push({
        id: `played_${c.matchId}`,
        kind: "result",
        title: `Played ${typeLabel}`,
        detail: `vs ${opp}${c.scoreSummary ? ` · ${c.scoreSummary}` : ""}`,
        at: c.completedAt || 0,
        tone: "muted",
      });
    }

    if (c.competitionId) {
      events.push({
        id: `tour_${c.matchId}`,
        kind: "tournament",
        title: "Tournament match",
        detail: c.competitionName || c.roundName || "Competition play",
        at: c.completedAt || 0,
        tone: "purple",
      });
    }
  });

  (milestones || []).forEach((m) => {
    events.push({
      id: `tl_${m.id}`,
      kind: "career_milestone",
      title: m.title,
      detail: m.detail,
      at: m.at || 0,
      tone: m.tone || "amber",
    });
  });

  (achievementBundle?.unlocked || []).slice(0, 12).forEach((a) => {
    events.push({
      id: `ach_${a.id}`,
      kind: "achievement",
      title: `Achievement unlocked · ${a.title}`,
      detail: a.detail,
      at: a.unlockedAt || list[0]?.completedAt || 0,
      tone: "purple",
    });
  });

  if ((stats.currentWinningStreak || 0) >= 3) {
    events.push({
      id: "tl_current_streak",
      kind: "streak",
      title: "New streak",
      detail: `${stats.currentWinningStreak}-match winning streak`,
      at: list[0]?.completedAt || Date.now(),
      tone: "win",
    });
  }

  const seen = new Set();
  return events
    .filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    })
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .slice(0, 32)
    .map((e) => ({
      ...e,
      whenLabel: formatMatchWhenShort(e.at),
    }));
}

/**
 * Empty-state copy for intelligence surfaces.
 * @param {{ sportFilter?: string, sportLabel?: string, matchesPlayed?: number }} ctx
 */
export function buildIntelligenceEmptyCopy(ctx = {}) {
  const matches = ctx.matchesPlayed || 0;
  const sportLabel = ctx.sportLabel || null;
  const isOverall = !ctx.sportFilter || ctx.sportFilter === "overall";

  if (matches === 0) {
    return {
      title: "No matches yet",
      body: sportLabel && !isOverall
        ? `Play your first ${sportLabel} match to start your journey.`
        : "Complete a match in any arena — every rally becomes part of your story.",
      ctaLabel: "Start an Arena",
      ctaTo: "/arena",
    };
  }
  if (matches < 10) {
    return {
      title: "Achievements unlocking",
      body: `Complete ${10 - matches} more match${10 - matches === 1 ? "" : "es"} to unlock the next career badge.`,
      ctaLabel: "View match history",
      ctaTo: "/profile/matches",
    };
  }
  return null;
}
